// ADR-199: one integrity check — its status, count, description and sample rows.
import React from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { FaChevronDown } from 'react-icons/fa';
import { getChipSx } from '../../../constants/chipPalette';
import { CHECK_STATUS_COLORS, CHECK_STATUSES } from '../../../constants/integrityChecks';
import {
  checkStatus, humanizeKey, sampleColumns, formatCell,
} from '../../../utils/integrityChecks';
import { LinkButton } from '../IngestionSyncRuns/styles';

const EMPLOYEE_COLUMN = 'employee_id';

const statusLabel = (result) => {
  const status = checkStatus(result);
  if (status === CHECK_STATUSES.VIOLATIONS) return `${result.count} ${result.count === 1 ? 'violation' : 'violations'}`;
  if (status === CHECK_STATUSES.INFO_ONLY) return `${result.informationalCount} informational`;
  return 'Passed';
};

const Cell = ({ column, value, onOpenEmployee }) => {
  if (column === EMPLOYEE_COLUMN && value && onOpenEmployee) {
    return (
      <TableCell>
        <LinkButton type="button" onClick={() => onOpenEmployee(value)} title="Open this person's contracts">
          {String(value)}
        </LinkButton>
      </TableCell>
    );
  }
  const { text, full } = formatCell(value);
  return (
    <TableCell sx={{ whiteSpace: text.length > 40 ? 'normal' : 'nowrap', maxWidth: 360 }}>
      {full ? <Tooltip arrow title={full}><span>{text}</span></Tooltip> : text}
    </TableCell>
  );
};

const SamplesTable = ({ result, onOpenEmployee }) => {
  const samples = result.samples || [];
  const columns = sampleColumns(samples);
  if (samples.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {result.informationalCount > 0
          ? 'Only informational rows (allowed by the Region’s settings); none are violations.'
          : 'No rows.'}
      </Typography>
    );
  }
  return (
    <>
      {result.count > samples.length && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {`Showing the first ${samples.length} of ${result.count}.`}
        </Typography>
      )}
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((c) => <TableCell key={c}>{humanizeKey(c)}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {samples.map((row, i) => (
              // Rows have no common id column; position is stable within one response.
              <TableRow key={`${result.check}-${i}`} hover>
                {columns.map((c) => <Cell key={c} column={c} value={row?.[c]} onOpenEmployee={onOpenEmployee} />)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const CheckCard = ({ result, onOpenEmployee }) => {
  const status = checkStatus(result);
  const hasRows = (result.samples || []).length > 0 || (result.informationalCount || 0) > 0;
  return (
    <Accordion
      disableGutters
      defaultExpanded={status === CHECK_STATUSES.VIOLATIONS}
      disabled={!hasRows}
      sx={{ '&.Mui-disabled': { backgroundColor: 'transparent' }, '&.Mui-disabled .MuiAccordionSummary-root': { opacity: 1 } }}
    >
      <AccordionSummary expandIcon={hasRows ? <FaChevronDown size={12} /> : null}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}>
          <Chip
            size="small"
            label={statusLabel(result)}
            sx={{ ...getChipSx(CHECK_STATUS_COLORS[status]), minWidth: 104, flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <Tooltip arrow title={result.check}>
              <Typography variant="subtitle2" component="span">{humanizeKey(result.check)}</Typography>
            </Tooltip>
            {result.description && (
              <Typography variant="body2" color="textSecondary">{result.description}</Typography>
            )}
            {status === CHECK_STATUSES.VIOLATIONS && result.informationalCount > 0 && (
              <Typography variant="caption" color="textSecondary">
                {`Plus ${result.informationalCount} informational, allowed by the Region’s settings.`}
              </Typography>
            )}
          </div>
        </div>
      </AccordionSummary>
      {hasRows && (
        <AccordionDetails>
          <SamplesTable result={result} onOpenEmployee={onOpenEmployee} />
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default CheckCard;
